import { useContext } from "react";

import log from "loglevel";
import { useQueryClient } from "react-query";

import * as userApi from "@/shared/apis/userApi";
import { AuthContext } from "@/shared/contexts/authContext";

import { UserExcludedError } from "../../errors";
import { useGlobalFilter } from "../useGlobalFilter";
import { getScopeRoutesOptions } from "./scopeRoutes";

export const useAuth = () => {
    const authContext = useContext(AuthContext);

    const queryClient = useQueryClient();

    const { removeGF } = useGlobalFilter();

    const auth = async (email: string, password: string) => {
        log.info("login", email);
        await userApi.auth(email, password);
    };

    const authItsme = async (itsmeUserId: string, itsmeToken: string) => {
        log.info("loginItsme", itsmeToken);
        await userApi.authItsme(itsmeUserId, itsmeToken);
    };

    const loginSilent = async (email: string, officeId: string, fromItsMe = false) => {
        log.info("loginSilent", officeId);

        try {
            const { token } = await userApi.loginSilent(officeId, fromItsMe);
            await authContext.login(token, email, officeId);
        } catch (e) {
            if (e?.code?.toLowerCase() === "brokerexcluded") {
                throw new UserExcludedError();
            }
            throw e;
        }
    };

    const changeBroker = async (officeId: string) => {
        await loginSilent(authContext.identity.email, officeId, authContext.identity.itsmeLogin);
        removeGF();
        queryClient.clear();
    };

    const loginProecoSilent = async (token: string) => {
        const newToken = token.replace("Bearer ", "");
        log.info("loginProecoSilent", newToken);

        await authContext.login(newToken);
    };

    const scopeOptions = getScopeRoutesOptions(authContext.identity.scope);

    return {
        prepare: authContext.init,
        changeBroker,
        auth,
        authItsme,
        loginScoped: authContext.loginScoped,
        loginSilent,
        loginProecoSilent,
        logout: authContext.logout,
        user: authContext.identity,
        userState: authContext.userState,
        scopeOptions,
    };
};
